import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@nesso/ui/lib/utils';

import { sessionByIdOptions } from '@/entities/session';
import {
    useIsGenerating,
    useIsGenerationAvailable,
    useSetFeedbackOptions,
} from '@/entities/session-note';
import { PopoverTooltipButton } from '@/shared/ui/common/popover-tooltip-button';

import { useSessionId } from '../../../../../model/use-session-id';
import { NoteFeedbackPopover } from './note-feedback-buttons/note-feedback-popover';

export function NoteFeedbackButtons() {
    const [feedbackOpen, setFeedbackOpen] = useState(false);

    const sessionId = useSessionId();
    const { data: session } = useQuery(sessionByIdOptions(sessionId));
    const noteId = session?.currentNote?.id;
    const feedback = session?.currentNote?.generator?.feedback ?? null;
    const isGenerating = useIsGenerating(sessionId);
    const isGenerationAvailable = useIsGenerationAvailable(sessionId);
    const isDisabled = isGenerating || (isGenerationAvailable && !session?.currentNote);
    const hasGenerationError = !!session?.currentNote?.generator?.errorType;
    const shouldBeHidden = hasGenerationError || !session?.currentNote?.generator;

    const setFeedbackMutation = useMutation(useSetFeedbackOptions(sessionId));

    const handleFeedback = (
        value: boolean | null,
        data?: { reasons: string[]; comment?: string },
    ) => {
        if (!noteId) return;
        setFeedbackMutation.mutate({
            id: noteId,
            feedback: value,
            ...(data?.reasons ? { reasons: data.reasons } : {}),
            ...(data?.comment ? { comment: data.comment } : {}),
        });
    };

    // While a feedback request is in flight, disable both buttons: the opposite one to avoid
    // conflicting feedback, and the same one to avoid re-firing the request that's already applied.
    const feedbackDisabled = isDisabled || setFeedbackMutation.isPending;

    if (shouldBeHidden) {
        return null;
    }

    return (
        <div className="flex items-center gap-2">
            <PopoverTooltipButton
                tooltipContent={<p>Good note</p>}
                tooltipContentProps={{ showArrow: false }}
                buttonProps={{
                    variant: 'ghost',
                    size: 'icon',
                    className: cn(
                        'hover:bg-secondary-hover hover:text-muted-foreground w-9 h-9 xs:w-6 xs:h-6',
                        feedback === true && 'text-secondary',
                    ),
                    disabled: feedbackDisabled,
                    onClick: () => {
                        if (feedbackOpen) return;
                        if (feedback === true) {
                            handleFeedback(null);
                            return;
                        }

                        handleFeedback(true);
                        toast('Thanks for your feedback.');
                    },
                }}
            >
                <ThumbsUp
                    className={cn(
                        'w-4 h-4',
                        feedback === true ? 'text-secondary' : 'text-muted-foreground',
                    )}
                />
            </PopoverTooltipButton>

            <PopoverTooltipButton
                open={feedbackOpen}
                onOpenChange={(open) => {
                    if (open && feedback !== false) setFeedbackOpen(true);
                }}
                popoverProps={{ modal: true }}
                tooltipContent={<p>Bad note</p>}
                tooltipContentProps={{ showArrow: false }}
                popoverContent={
                    <NoteFeedbackPopover
                        onSubmit={(data) => {
                            handleFeedback(false, {
                                reasons: data.reasons,
                                comment: data.feedback || undefined,
                            });
                            setFeedbackOpen(false);
                            toast('Thank you for helping to improve Nesso.');
                        }}
                        onSkip={() => {
                            handleFeedback(false);
                            setFeedbackOpen(false);
                            toast('Thank you for helping to improve Nesso.');
                        }}
                        onClose={() => {
                            setFeedbackOpen(false);
                        }}
                    />
                }
                wrapPopoverContent={false}
                buttonProps={{
                    variant: 'ghost',
                    size: 'icon',
                    className: cn(
                        'hover:bg-secondary-hover hover:text-muted-foreground w-9 h-9 xs:w-6 xs:h-6',
                        feedbackOpen && 'bg-secondary-hover text-muted-foreground',
                    ),
                    disabled: feedbackDisabled,
                    onClick: () => {
                        if (feedback === false) {
                            handleFeedback(null);
                        }
                    },
                }}
            >
                <ThumbsDown
                    className={cn(
                        'w-4 h-4',
                        feedback === false ? 'text-secondary' : 'text-muted-foreground',
                    )}
                />
            </PopoverTooltipButton>
        </div>
    );
}
